<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    Daily Usage
                </h4>
            </div>
        </div>
        <b-row>
            <b-col
                lg="8"
                class="pr-0"
            >
                <div
                    ref="REF_USAGE_DAILY"
                >
                    <VsSvgLine
                        :data="chartData"
                        xKey="date"
                        :yKeys="['usage']"
                        :lineColor="lineColor"
                        :width="width"
                        :height="height"
                        :labelRotate="0"
                        :tickSize="0.7"
                        :tickPadding="15"
                        :strokeWidth="3"
                        :yGrid="6"
                        :padding="padding"
                        area
                        circle
                        xAxis
                        yAxis
                        xLabel
                        yLabel
                        :labelColor="themeColors.currentColor"
                        @mouseover:point="hoverPoint"
                        @mouseleave.native="outPoint"
                    >
                        <template v-slot:x-axis="{ item }">
                            <template v-if="chartData.length >= 50 && item.index % 14 === 0">
                                {{ item.label.substring(5, 10) }}
                            </template>
                            <template v-else-if="chartData.length < 50 && chartData.length >= 30 && item.index % 7 === 0">
                                {{ item.label.substring(5, 10) }}
                            </template>
                            <template v-else-if="chartData.length < 30 && chartData.length >= 15 && item.index % 5 === 0">
                                {{ item.label.substring(5, 10) }}
                            </template>
                            <template v-else-if="chartData.length < 15 && item.index % 2 === 0">
                                {{ item.label.substring(5, 10) }}
                            </template>
                        </template>
                        <template v-slot:y-axis="{ item }">
                            {{ __toKFormat(item.label) }}
                        </template>
                        <template v-slot:tooltip="{ item }">
                            <g v-if="tooltipData.index > -1">
                                <rect
                                    :x="tooltipData.index < chartData.length / 2 ? item.x + 10 : item.x - 160"
                                    :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17 + 35) - padding : padding + 5"
                                    :fill="backgroundColor"
                                    :height="yKeys.length * 17 + 27"
                                    width="150"
                                    opacity="0.9"
                                    rx="5"
                                    ry="5"
                                ></rect>
                                <text
                                    font-size="12"
                                    font-weight="bold"
                                    :fill="themeColors.currentColor"
                                    :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                                    :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                                    :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17) - padding * 2 : padding * 2"
                                >
                                    {{ item.label }}
                                </text>
                                <text
                                    font-size="14"
                                    font-weight="bold"
                                    :fill="themeColors.currentColor"
                                    :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                                    :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                                    :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17) - padding * 2 + (0 * 17 + 20) : padding * 2 + (0 * 17 + 20)"
                                >
                                    {{ item.data.usage }}
                                </text>
                            </g>
                        </template>
                    </VsSvgLine>
                </div>
            </b-col>
            <b-col
                lg="4"
                class="pl-0"
            >
                <div class="b-table-sticky-header table-responsive" style="max-height:200px;">
                    <b-table-simple
                        no-border-collapse
                    >
                        <b-thead>
                            <b-tr>
                                <b-th>
                                    Day
                                </b-th>
                                <b-th>
                                    Usage
                                </b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr v-for="(item, index) in chartData" :key="index">
                                <b-td>
                                    {{ item.date }}
                                </b-td>
                                <b-td>
                                    {{ item.usage }}
                                </b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </div>
            </b-col>
        </b-row>
    </b-card>
</template>

<script>
import { computed, getCurrentInstance, toRefs, ref, onUpdated, onMounted, onUnmounted } from '@vue/composition-api'
import { VsSvgLine } from '@/libs/vuesign/chart'
import useCommonData from '@/composables/useCommonData'
export default {
    components: {
        VsSvgLine
    },
    props: {
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        chartData: {
            type: Array,
            default: function () {
                return []
            }
        },
        theme: {
            type: String
        }
    },
    setup (props, context) {
        const { __toKFormat } = useCommonData()
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const STORE_KEY = '$_open_api'
        const { themeColors, theme } = toRefs(props)
        const yKeys = ref(['usage'])
        const width = ref(0)
        const height = 200
        const padding = 20
        const daily = computed(() => store.getters[`${STORE_KEY}/dailyUsage`])
        const backgroundColor = computed(() => themeColors.value[theme.value])
        const lineColor = {
            usage: themeColors.value.primary
        }

        // tooltip
        const tooltipData = ref({})
        const hoverPoint = ({ event, data }) => {
            tooltipData.value = data
        }
        const outPoint = () => {
            tooltipData.value = {}
        }

        // resize
        const REF_USAGE_DAILY = ref(null)
        const resize = () => {
            if (REF_USAGE_DAILY.value) {
                width.value = REF_USAGE_DAILY.value.getBoundingClientRect().width
            }
        }
        onUpdated(resize)
        onMounted(() => {
            vm.$nextTick(() => {
                resize()
                window.addEventListener('resize', resize)
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', resize)
        })

        return {
            REF_USAGE_DAILY,
            yKeys,
            daily,
            lineColor,
            tooltipData,
            hoverPoint,
            outPoint,
            width,
            height,
            padding,
            __toKFormat,
            backgroundColor
        }
    }
}
</script>

<style>

</style>
